import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const ROW_COUNT = 15
const REFRESH_INTERVAL_MS = 5000
const IDLE_BEFORE_REFRESH_MS = 2000

const createEmptyRow = index => ({
  id: index + 1,
  guide: '',
  driver: '',
  busReg: '',
  totalPax: '',
})

const createEmptyRows = () => Array.from({ length: ROW_COUNT }, (_, index) => createEmptyRow(index))

const normalizeRows = rowData => createEmptyRows().map((row, index) => ({
  ...row,
  ...(Array.isArray(rowData) ? rowData[index] : {}),
  id: index + 1,
  totalPax: Array.isArray(rowData) && rowData[index]?.totalPax != null ? String(rowData[index].totalPax) : '',
}))

const toPayload = rows => rows.map((row, index) => ({
  slotNumber: index + 1,
  guide: row.guide,
  driver: row.driver,
  busReg: row.busReg,
  totalPax: row.totalPax === '' ? null : Number(row.totalPax),
}))

function OnTheRoadTodayPage() {
  const [guides, setGuides] = useState([])
  const [rows, setRows] = useState(createEmptyRows)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveState, setSaveState] = useState('')
  const hydratedRef = useRef(false)
  const lastEditAtRef = useRef(0)
  const lastSavedPayloadRef = useRef('')
  const isFetchingBoardRef = useRef(false)

  const guideApiUrl = `${process.env.REACT_APP_API_URL}/api/guides`
  const onTheRoadApiUrl = `${process.env.REACT_APP_API_URL}/api/on-the-road-today`

  const loadBoard = useCallback(async ({ silent = false } = {}) => {
    if (isFetchingBoardRef.current) return
    isFetchingBoardRef.current = true

    try {
      const res = await fetch(onTheRoadApiUrl, {
        credentials: 'include',
        cache: 'no-store',
      })
      if (!res.ok) throw new Error(`Board load failed: ${res.status}`)

      const rowData = await res.json()
      const nextRows = normalizeRows(rowData)
      const nextPayload = JSON.stringify(toPayload(nextRows))

      lastSavedPayloadRef.current = nextPayload
      setRows(currentRows => {
        const currentPayload = JSON.stringify(toPayload(currentRows))
        const isIdle = Date.now() - lastEditAtRef.current > IDLE_BEFORE_REFRESH_MS
        if (!hydratedRef.current || isIdle || currentPayload === lastSavedPayloadRef.current) {
          return nextRows
        }
        return currentRows
      })

      if (!silent) setError('')
    } catch (err) {
      if (!silent) setError(err.message)
      throw err
    } finally {
      isFetchingBoardRef.current = false
    }
  }, [onTheRoadApiUrl])

  useEffect(() => {
    Promise.all([
      fetch(guideApiUrl, {
        credentials: 'include',
      }).then(res => {
        if (!res.ok) throw new Error(`Guide load failed: ${res.status}`)
        return res.json()
      }),
      loadBoard(),
    ])
      .then(([guideData]) => {
        setGuides(Array.isArray(guideData) ? guideData : [])
        hydratedRef.current = true
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [guideApiUrl, loadBoard])

  useEffect(() => {
    if (!hydratedRef.current) return

    const refreshBoard = () => {
      if (Date.now() - lastEditAtRef.current <= IDLE_BEFORE_REFRESH_MS) return
      loadBoard({ silent: true }).catch(() => {})
    }

    const intervalId = window.setInterval(refreshBoard, REFRESH_INTERVAL_MS)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshBoard()
    }

    window.addEventListener('focus', refreshBoard)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refreshBoard)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [loadBoard])

  useEffect(() => {
    if (!hydratedRef.current || loading) return

    const payload = JSON.stringify(toPayload(rows))
    if (payload === lastSavedPayloadRef.current) {
      if (saveState !== 'Saved') setSaveState('Saved')
      return
    }

    setSaveState('Saving...')

    const timeoutId = setTimeout(() => {
      fetch(onTheRoadApiUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      })
        .then(res => {
          if (!res.ok) throw new Error(`Save failed: ${res.status}`)
          return res.json()
        })
        .then(savedRows => {
          lastSavedPayloadRef.current = JSON.stringify(toPayload(normalizeRows(savedRows)))
          setSaveState('Saved')
          setError('')
          return loadBoard({ silent: true })
        })
        .catch(err => {
          console.error('Failed to save On the Road Today rows:', err)
          setSaveState('Save failed')
          setError(err.message)
        })
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [rows, loading, onTheRoadApiUrl, saveState, loadBoard])

  const guideOptions = useMemo(() => {
    return [...new Set(guides.map(guide => String(guide.userName || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  }, [guides])

  const handleFieldChange = (rowId, field, value) => {
    setRows(currentRows => currentRows.map(row => (
      row.id === rowId ? { ...row, [field]: value } : row
    )))
  }

  const clearAllRows = () => {
    if (!window.confirm('Clear all 15 rows?')) return
    setRows(createEmptyRows())
  }

  if (loading) return <div className="loading">Loading On the Road today...</div>
  if (error) return <div className="error">Error: {error}</div>

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <p className="text-uppercase text-muted small mb-2">Daily planning</p>
          <h1 className="h3 mb-2">On the Road today</h1>
          <p className="text-muted mb-1">Pick the guide username, then fill in driver, bus reg, and total pax manually.</p>
          <p className="small mb-0 text-info">Shared live board, {saveState || 'ready'}</p>
        </div>
        <button type="button" className="btn btn-outline-danger" onClick={clearAllRows}>
          Clear all rows
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-dark table-striped table-bordered align-middle">
          <thead>
            <tr>
              <th style={{ minWidth: '72px' }}>Row</th>
              <th style={{ minWidth: '220px' }}>Guide working today</th>
              <th style={{ minWidth: '200px' }}>Driver</th>
              <th style={{ minWidth: '180px' }}>Bus Reg</th>
              <th style={{ minWidth: '96px', width: '96px' }}>Total Pax</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>
                  <select
                    className="form-select"
                    value={row.guide}
                    onChange={e => handleFieldChange(row.id, 'guide', e.target.value)}
                  >
                    <option value="">Select guide</option>
                    {guideOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="form-control"
                    type="text"
                    value={row.driver}
                    onChange={e => handleFieldChange(row.id, 'driver', e.target.value)}
                    placeholder="Driver name"
                  />
                </td>
                <td>
                  <input
                    className="form-control"
                    type="text"
                    value={row.busReg}
                    onChange={e => handleFieldChange(row.id, 'busReg', e.target.value.toUpperCase())}
                    placeholder="Bus registration"
                  />
                </td>
                <td style={{ width: '96px' }}>
                  <input
                    className="form-control"
                    style={{ minWidth: '72px', maxWidth: '96px' }}
                    type="number"
                    min="0"
                    step="1"
                    value={row.totalPax}
                    onChange={e => handleFieldChange(row.id, 'totalPax', e.target.value)}
                    placeholder="0"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default OnTheRoadTodayPage
